import { type ReactElement } from 'react';
import { CalendarDays, List, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation, type CopyKey } from '@/lib/i18n';

export const VENUE_VIEWS = ['map', 'list', 'calendar'] as const;

export type VenueView = (typeof VENUE_VIEWS)[number];

const TABS: Readonly<Record<VenueView, { label: CopyKey; icon: typeof Map }>> = {
  map: { label: 'viewMap', icon: Map },
  list: { label: 'viewList', icon: List },
  calendar: { label: 'viewCalendar', icon: CalendarDays },
};

// One row, labels dropping before it wraps, like every control row. §20
export function ViewTabs({
  view,
  onPick,
}: {
  view: VenueView;
  onPick: (view: VenueView) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div role="tablist" aria-label={t('views')} className="flex gap-1 px-2 sm:px-3">
      {VENUE_VIEWS.map((each) => {
        const tab = TABS[each];
        return (
          <Button
            key={each}
            size="sm"
            role="tab"
            aria-selected={view === each}
            variant={view === each ? 'default' : 'outline'}
            className="rounded-full"
            onClick={() => onPick(each)}
          >
            <tab.icon className="size-4" aria-hidden />
            <span className="hidden sm:inline">{t(tab.label)}</span>
          </Button>
        );
      })}
    </div>
  );
}
